import type { BrowserDisplayMetrics } from './browserDisplayMetrics';
export type ModerationRole = 'moderator' | 'administrator';
export type ModerationPlayerSort = 'username' | 'level' | 'village_name' | 'online' | 'ban_count';
export type ModerationSortDirection = 'asc' | 'desc';

export type ModerationDashboardPayload = Readonly<{
  role: ModerationRole;
  ghostMode: boolean;
  speedBoostMode?: boolean;
  onlinePlayers: number;
  totalPlayers: number;
  villages: number;
  activeBans: number;
  activeAccountBans: number;
  activeIpBans: number;
  generatedAt: number;
}>;

export type ModerationServerStatusPayload = Readonly<{
  timestampMs: number;
  uptimeSeconds: number;
  cpu: Readonly<{
    available: boolean;
    logicalCores: number;
    totalTicks: number;
    idleTicks: number;
  }>;
  memory: Readonly<{
    available: boolean;
    totalBytes: number;
    usedBytes: number;
    availableBytes: number;
  }>;
  storage: Readonly<{
    available: boolean;
    path: string;
    totalBytes: number;
    usedBytes: number;
    availableBytes: number;
  }>;
  network: Readonly<{
    available: boolean;
    interfaceCount: number;
    receivedBytes: number;
    transmittedBytes: number;
    last30DaysAvailable: boolean;
    last30DaysReceivedBytes: number;
    last30DaysTransmittedBytes: number;
    last30DaysTotalBytes: number;
  }>;
  apiTraffic: Readonly<{
    requestCount: number;
    responseBytes: number;
  }>;
  persistence: Readonly<{
    lastDurationMs: number;
    lastBuildings: number;
    lastTrees: number;
    lastProgressionRows: number;
  }>;
  process: Readonly<{
    goroutines: number;
    heapAllocBytes: number;
    heapInUseBytes: number;
  }>;
}>;

export type ModerationPlayerSummaryPayload = Readonly<{
  playerId: number;
  username: string;
  level: number;
  villageId: number;
  villageName: string;
  online: boolean;
  moderator: boolean;
  administrator: boolean;
  banCount: number;
}>;

export type ModerationPlayerBanPayload = Readonly<{
  playerId: number;
  username: string;
}>;

export type ModerationSharedIPPlayerPayload = Readonly<{
  playerId: number;
  username: string;
  sharedIps: readonly string[];
  online: boolean;
}>;

export type ModerationPlayerDetailPayload = ModerationPlayerSummaryPayload &
  Readonly<{
    browserSessions: readonly Readonly<{
      startedAt: number;
      lastSeenAt: number;
      userAgent: string;
      display: BrowserDisplayMetrics | null;
    }>[];
    statusMessage: string;
    recentIps: readonly string[];
    recentIpLogins: readonly Readonly<{ ip: string; lastUsedAt: number }>[];
    previousBans: readonly ModerationBanPayload[];
    sharedIpPlayers: readonly ModerationSharedIPPlayerPayload[];
    bannedBy: readonly ModerationPlayerBanPayload[];
    moderatorNote: ModerationPlayerNotePayload;
    sillyGoose: boolean;
    sillyGooseExpiresAt: number;
    onlineTime: Readonly<{
      totalMilliseconds: number;
      activeMilliseconds: number;
      afkMilliseconds: number;
    }>;
  }>;

export type ModerationPlayerNotePayload = Readonly<{
  text: string;
  updatedBy: string;
  updatedAt: number;
}>;

export type ModerationPlayerListPayload = Readonly<{
  players: readonly ModerationPlayerSummaryPayload[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}>;

export type ModerationOnlinePlayersPayload = Readonly<{
  players: readonly ModerationPlayerSummaryPayload[];
  onlineCount: number;
  generatedAt: number;
}>;

export type ModerationCommunicationPayload = Readonly<{
  communicationType: string;
  chatScope: string;
  fromPlayerId: number;
  fromUsername: string;
  toPlayerId?: number;
  toUsername?: string;
  villageId?: number;
  private?: boolean;
  messageText: string;
  createdAt: number;
  hiddenInGame: boolean;
}>;

export type ModerationBanPayload = Readonly<{
  id: number;
  playerId: number;
  username: string;
  message: string;
  blockedUntil: number;
  createdAt: number;
  status: 'active' | 'expired' | 'revoked';
  ip?: string;
}>;

export type ModerationActionPayload = Readonly<{
  ok: boolean;
  statusMessage?: string;
  ghostMode?: boolean;
  speedBoostMode?: boolean;
  queued?: number;
  wasOnline?: boolean;
  ban?: ModerationBanPayload;
  moderatorNote?: ModerationPlayerNotePayload;
  sillyGoose?: boolean;
  sillyGooseExpiresAt?: number;
}>;

export function parseModerationDashboardResponse(raw: unknown): ModerationDashboardPayload {
  return parseDashboard(record(raw, 'moderation response').dashboard);
}

export function parseModerationServerStatusResponse(raw: unknown): ModerationServerStatusPayload {
  const value = record(raw, 'moderation server status');
  const cpu = record(value.cpu, 'moderation server status cpu');
  const memory = record(value.memory, 'moderation server status memory');
  const storage = record(value.storage, 'moderation server status storage');
  const network = record(value.network, 'moderation server status network');
  const apiTraffic = record(value.apiTraffic, 'moderation server status API traffic');
  const persistence = record(value.persistence, 'moderation server status persistence');
  const process = record(value.process, 'moderation server status process');
  return {
    timestampMs: nonNegativeNumber(value.timestampMs, 'moderation server status timestampMs'),
    uptimeSeconds: nonNegativeNumber(value.uptimeSeconds, 'moderation server status uptimeSeconds'),
    cpu: {
      available: boolean(cpu.available, 'moderation server status cpu.available'),
      logicalCores: nonNegativeNumber(
        cpu.logicalCores,
        'moderation server status cpu.logicalCores',
      ),
      totalTicks: nonNegativeNumber(cpu.totalTicks, 'moderation server status cpu.totalTicks'),
      idleTicks: nonNegativeNumber(cpu.idleTicks, 'moderation server status cpu.idleTicks'),
    },
    memory: {
      available: boolean(memory.available, 'moderation server status memory.available'),
      totalBytes: nonNegativeNumber(
        memory.totalBytes,
        'moderation server status memory.totalBytes',
      ),
      usedBytes: nonNegativeNumber(memory.usedBytes, 'moderation server status memory.usedBytes'),
      availableBytes: nonNegativeNumber(
        memory.availableBytes,
        'moderation server status memory.availableBytes',
      ),
    },
    storage: {
      available: boolean(storage.available, 'moderation server status storage.available'),
      path: text(storage.path, 'moderation server status storage.path'),
      totalBytes: nonNegativeNumber(
        storage.totalBytes,
        'moderation server status storage.totalBytes',
      ),
      usedBytes: nonNegativeNumber(storage.usedBytes, 'moderation server status storage.usedBytes'),
      availableBytes: nonNegativeNumber(
        storage.availableBytes,
        'moderation server status storage.availableBytes',
      ),
    },
    network: {
      available: boolean(network.available, 'moderation server status network.available'),
      interfaceCount: nonNegativeNumber(
        network.interfaceCount,
        'moderation server status network.interfaceCount',
      ),
      receivedBytes: nonNegativeNumber(
        network.receivedBytes,
        'moderation server status network.receivedBytes',
      ),
      transmittedBytes: nonNegativeNumber(
        network.transmittedBytes,
        'moderation server status network.transmittedBytes',
      ),
      last30DaysAvailable: boolean(
        network.last30DaysAvailable,
        'moderation server status network.last30DaysAvailable',
      ),
      last30DaysReceivedBytes: nonNegativeNumber(
        network.last30DaysReceivedBytes,
        'moderation server status network.last30DaysReceivedBytes',
      ),
      last30DaysTransmittedBytes: nonNegativeNumber(
        network.last30DaysTransmittedBytes,
        'moderation server status network.last30DaysTransmittedBytes',
      ),
      last30DaysTotalBytes: nonNegativeNumber(
        network.last30DaysTotalBytes,
        'moderation server status network.last30DaysTotalBytes',
      ),
    },
    apiTraffic: {
      requestCount: nonNegativeNumber(
        apiTraffic.requestCount,
        'moderation server status API traffic.requestCount',
      ),
      responseBytes: nonNegativeNumber(
        apiTraffic.responseBytes,
        'moderation server status API traffic.responseBytes',
      ),
    },
    persistence: {
      lastDurationMs: optionalNonNegativeNumber(
        persistence.lastDurationMs,
        'moderation server status persistence.lastDurationMs',
      ),
      lastBuildings: optionalNonNegativeNumber(
        persistence.lastBuildings,
        'moderation server status persistence.lastBuildings',
      ),
      lastTrees: optionalNonNegativeNumber(
        persistence.lastTrees,
        'moderation server status persistence.lastTrees',
      ),
      lastProgressionRows: optionalNonNegativeNumber(
        persistence.lastProgressionRows,
        'moderation server status persistence.lastProgressionRows',
      ),
    },
    process: {
      goroutines: nonNegativeNumber(
        process.goroutines,
        'moderation server status process.goroutines',
      ),
      heapAllocBytes: nonNegativeNumber(
        process.heapAllocBytes,
        'moderation server status process.heapAllocBytes',
      ),
      heapInUseBytes: nonNegativeNumber(
        process.heapInUseBytes,
        'moderation server status process.heapInUseBytes',
      ),
    },
  };
}

export function parseModerationOnlinePlayersResponse(raw: unknown): ModerationOnlinePlayersPayload {
  const response = record(raw, 'moderation online response');
  return {
    players: array(response.players, 'moderation online players').map((player, index) =>
      parsePlayer(player, `moderation online players[${String(index)}]`),
    ),
    onlineCount: integer(response.onlineCount, 'moderation online count'),
    generatedAt: integer(response.generatedAt, 'moderation online generatedAt'),
  };
}

export function parseModerationPlayerListResponse(raw: unknown): ModerationPlayerListPayload {
  const response = record(raw, 'moderation player list');
  return {
    players: array(response.players, 'moderation players').map((player, index) =>
      parsePlayer(player, `moderation players[${String(index)}]`),
    ),
    page: integer(response.page, 'moderation player page'),
    limit: integer(response.limit, 'moderation player limit'),
    total: integer(response.total, 'moderation player total'),
    totalPages: integer(response.totalPages, 'moderation player totalPages'),
  };
}

export function parseModerationPlayerResponse(raw: unknown): ModerationPlayerDetailPayload {
  const value = record(record(raw, 'moderation player response').player, 'moderation player');
  const onlineTime = record(value.onlineTime, 'moderation player onlineTime');
  const totalMilliseconds = integer(
    onlineTime.totalMilliseconds,
    'moderation player onlineTime totalMilliseconds',
  );
  const activeMilliseconds = integer(
    onlineTime.activeMilliseconds,
    'moderation player onlineTime activeMilliseconds',
  );
  const afkMilliseconds = integer(
    onlineTime.afkMilliseconds,
    'moderation player onlineTime afkMilliseconds',
  );
  const moderatorNote = parseModeratorNote(value.moderatorNote, 'moderation player moderatorNote');
  const player = parsePlayer(value, 'moderation player');
  const bannedBy = array(value.bannedBy, 'moderation player bannedBy').map((entry, index) => {
    const label = `moderation player bannedBy[${String(index)}]`;
    const blocker = record(entry, label);
    return {
      playerId: integer(blocker.playerId, `${label}.playerId`),
      username: text(blocker.username, `${label}.username`),
    };
  });
  const sharedIpPlayers = array(value.sharedIpPlayers, 'moderation player sharedIpPlayers').map(
    (entry, index) => {
      const label = `moderation player sharedIpPlayers[${String(index)}]`;
      const relatedPlayer = record(entry, label);
      return {
        playerId: integer(relatedPlayer.playerId, `${label}.playerId`),
        username: text(relatedPlayer.username, `${label}.username`),
        sharedIps: array(relatedPlayer.sharedIps, `${label}.sharedIps`).map((ip, ipIndex) =>
          text(ip, `${label}.sharedIps[${String(ipIndex)}]`),
        ),
        online: boolean(relatedPlayer.online, `${label}.online`),
      };
    },
  );
  if (
    totalMilliseconds < 0 ||
    activeMilliseconds < 0 ||
    afkMilliseconds < 0 ||
    totalMilliseconds !== activeMilliseconds + afkMilliseconds
  ) {
    throw new Error('moderation player onlineTime is invalid.');
  }
  if (player.banCount !== bannedBy.length) {
    throw new Error('moderation player banCount does not match bannedBy.');
  }
  return {
    ...player,
    browserSessions: array(value.browserSessions ?? [], 'moderation player browserSessions').map(
      (entry, index) => {
        const label = `moderation player browserSessions[${String(index)}]`;
        const session = record(entry, label);
        return {
          startedAt: integer(session.startedAt, `${label}.startedAt`),
          lastSeenAt: integer(session.lastSeenAt, `${label}.lastSeenAt`),
          userAgent: text(session.userAgent, `${label}.userAgent`),
          display: parseBrowserDisplayMetrics(session.display, `${label}.display`),
        };
      },
    ),
    statusMessage:
      value.statusMessage === undefined
        ? ''
        : text(value.statusMessage, 'moderation player statusMessage'),
    recentIps: array(value.recentIps, 'moderation player recentIps').map((ip, index) =>
      text(ip, `moderation player recentIps[${String(index)}]`),
    ),
    recentIpLogins: array(
      value.recentIpLogins ??
        array(value.recentIps, 'moderation player recentIps').map((ip) => ({ ip, lastUsedAt: 0 })),
      'moderation player recentIpLogins',
    )
      .map((entry, index) => {
        const label = `moderation player recentIpLogins[${String(index)}]`;
        const login = record(entry, label);
        return {
          ip: text(login.ip, `${label}.ip`),
          lastUsedAt: optionalNonNegativeNumber(login.lastUsedAt, `${label}.lastUsedAt`),
        };
      })
      .sort((a, b) => b.lastUsedAt - a.lastUsedAt),
    previousBans: array(value.previousBans ?? [], 'moderation player previousBans').map(
      (entry, index) => parseBan(entry, `moderation player previousBans[${String(index)}]`),
    ),
    sharedIpPlayers,
    bannedBy,
    moderatorNote,
    sillyGoose: boolean(value.sillyGoose, 'moderation player sillyGoose'),
    sillyGooseExpiresAt: optionalNonNegativeNumber(
      value.sillyGooseExpiresAt,
      'moderation player sillyGooseExpiresAt',
    ),
    onlineTime: { totalMilliseconds, activeMilliseconds, afkMilliseconds },
  };
}

export function parseModerationCommunicationsResponse(
  raw: unknown,
): readonly ModerationCommunicationPayload[] {
  const response = record(raw, 'moderation communications response');
  return array(response.communications, 'moderation communications').map((entry, index) => {
    const label = `moderation communications[${String(index)}]`;
    const value = record(entry, label);
    return {
      communicationType: text(value.communicationType, `${label}.communicationType`),
      chatScope: text(value.chatScope, `${label}.chatScope`),
      fromPlayerId: integer(value.fromPlayerId, `${label}.fromPlayerId`),
      fromUsername: text(value.fromUsername, `${label}.fromUsername`),
      ...(value.toPlayerId === undefined
        ? {}
        : { toPlayerId: integer(value.toPlayerId, `${label}.toPlayerId`) }),
      ...(value.toUsername === undefined
        ? {}
        : { toUsername: text(value.toUsername, `${label}.toUsername`) }),
      ...(value.villageId === undefined
        ? {}
        : { villageId: integer(value.villageId, `${label}.villageId`) }),
      ...(value.isPrivate === undefined
        ? {}
        : { private: boolean(value.isPrivate, `${label}.isPrivate`) }),
      messageText: text(value.messageText, `${label}.messageText`),
      createdAt: integer(value.createdAt, `${label}.createdAt`),
      hiddenInGame: boolean(value.hiddenInGame, `${label}.hiddenInGame`),
    };
  });
}

export function parseModerationBansResponse(raw: unknown): readonly ModerationBanPayload[] {
  const response = record(raw, 'moderation bans response');
  return array(response.bans, 'moderation bans').map((entry, index) =>
    parseBan(entry, `moderation bans[${String(index)}]`),
  );
}

export function parseModerationActionResponse(raw: unknown): ModerationActionPayload {
  const response = record(raw, 'moderation action response');
  return {
    ok: boolean(response.ok, 'moderation action ok'),
    ...(response.statusMessage === undefined
      ? {}
      : { statusMessage: text(response.statusMessage, 'moderation action statusMessage') }),
    ...(response.speedBoostMode === undefined
      ? {}
      : { speedBoostMode: boolean(response.speedBoostMode, 'moderation action speedBoostMode') }),
    ...(response.ghostMode === undefined
      ? {}
      : { ghostMode: boolean(response.ghostMode, 'moderation action ghostMode') }),
    ...(response.sillyGoose === undefined
      ? {}
      : { sillyGoose: boolean(response.sillyGoose, 'moderation action sillyGoose') }),
    ...(response.sillyGooseExpiresAt === undefined
      ? {}
      : {
          sillyGooseExpiresAt: nonNegativeNumber(
            response.sillyGooseExpiresAt,
            'moderation action sillyGooseExpiresAt',
          ),
        }),
    ...(response.queued === undefined
      ? {}
      : { queued: integer(response.queued, 'moderation action queued') }),
    ...(response.wasOnline === undefined
      ? {}
      : { wasOnline: boolean(response.wasOnline, 'moderation action wasOnline') }),
    ...(response.ban === undefined ? {} : { ban: parseBan(response.ban, 'moderation action ban') }),
    ...(response.moderatorNote === undefined
      ? {}
      : {
          moderatorNote: parseModeratorNote(
            response.moderatorNote,
            'moderation action moderatorNote',
          ),
        }),
  };
}

function parseModeratorNote(raw: unknown, label: string): ModerationPlayerNotePayload {
  const value = record(raw, label);
  return {
    text: text(value.text, `${label}.text`),
    updatedBy: text(value.updatedBy, `${label}.updatedBy`),
    updatedAt: nonNegativeNumber(value.updatedAt, `${label}.updatedAt`),
  };
}

function parseDashboard(raw: unknown): ModerationDashboardPayload {
  const value = record(raw, 'moderation dashboard');
  const role = text(value.role, 'moderation dashboard role');
  if (role !== 'moderator' && role !== 'administrator') {
    throw new Error('The moderation dashboard returned an unsupported role.');
  }
  return {
    role,
    ghostMode: boolean(value.ghostMode, 'moderation dashboard ghostMode'),
    speedBoostMode:
      value.speedBoostMode === undefined
        ? false
        : boolean(value.speedBoostMode, 'moderation dashboard speedBoostMode'),
    onlinePlayers: integer(value.onlinePlayers, 'moderation dashboard onlinePlayers'),
    totalPlayers: integer(value.totalPlayers, 'moderation dashboard totalPlayers'),
    villages: integer(value.villages, 'moderation dashboard villages'),
    activeBans: integer(value.activeBans, 'moderation dashboard activeBans'),
    activeAccountBans: integer(value.activeAccountBans, 'moderation dashboard activeAccountBans'),
    activeIpBans: integer(value.activeIpBans, 'moderation dashboard activeIpBans'),
    generatedAt: integer(value.generatedAt, 'moderation dashboard generatedAt'),
  };
}

function parsePlayer(raw: unknown, label: string): ModerationPlayerSummaryPayload {
  const value = record(raw, label);
  const banCount = integer(value.banCount, `${label}.banCount`);
  if (banCount < 0) throw new Error(`${label}.banCount must not be negative.`);
  return {
    playerId: integer(value.playerId, `${label}.playerId`),
    username: text(value.username, `${label}.username`),
    level: integer(value.level, `${label}.level`),
    villageId: integer(value.villageId, `${label}.villageId`),
    villageName: text(value.villageName, `${label}.villageName`),
    online: boolean(value.online, `${label}.online`),
    moderator: boolean(value.moderator, `${label}.moderator`),
    administrator: boolean(value.administrator, `${label}.administrator`),
    banCount,
  };
}

function parseBan(raw: unknown, label: string): ModerationBanPayload {
  const value = record(raw, label);
  const status = text(value.status, `${label}.status`);
  if (status !== 'active' && status !== 'expired' && status !== 'revoked') {
    throw new Error(`${label}.status is invalid.`);
  }
  return {
    id: integer(value.id, `${label}.id`),
    playerId: integer(value.playerId, `${label}.playerId`),
    username: text(value.username, `${label}.username`),
    message: text(value.message, `${label}.message`),
    blockedUntil: integer(value.blockedUntil, `${label}.blockedUntil`),
    createdAt: integer(value.createdAt, `${label}.createdAt`),
    status,
    ...(value.ip === undefined ? {} : { ip: text(value.ip, `${label}.ip`) }),
  };
}

function record(raw: unknown, label: string): Record<string, unknown> {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error(`${label} must be an object.`);
  }
  return raw as Record<string, unknown>;
}

function array(raw: unknown, label: string): readonly unknown[] {
  if (!Array.isArray(raw)) throw new Error(`${label} must be an array.`);
  return raw;
}

function text(raw: unknown, label: string): string {
  if (typeof raw !== 'string') throw new Error(`${label} must be text.`);
  return raw;
}

function integer(raw: unknown, label: string): number {
  if (typeof raw !== 'number' || !Number.isSafeInteger(raw)) {
    throw new Error(`${label} must be an integer.`);
  }
  return raw;
}

function nonNegativeNumber(raw: unknown, label: string): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw) || raw < 0) {
    throw new Error(`${label} must be a non-negative number.`);
  }
  return raw;
}

function optionalNonNegativeNumber(raw: unknown, label: string): number {
  return raw === undefined ? 0 : nonNegativeNumber(raw, label);
}

function boolean(raw: unknown, label: string): boolean {
  if (typeof raw !== 'boolean') throw new Error(`${label} must be a boolean.`);
  return raw;
}

function parseBrowserDisplayMetrics(raw: unknown, label: string): BrowserDisplayMetrics | null {
  if (raw === undefined || raw === null) return null;
  const value = record(raw, label);
  const size = (key: string): number => {
    const result = integer(value[key], `${label}.${key}`);
    if (result < 0 || result > 100000) throw new Error(`${label}.${key} is invalid.`);
    return result;
  };
  const pixelRatio = value.pixelRatio;
  if (
    typeof pixelRatio !== 'number' ||
    !Number.isFinite(pixelRatio) ||
    pixelRatio <= 0 ||
    pixelRatio > 32
  )
    throw new Error(`${label}.pixelRatio is invalid.`);
  return {
    screenWidth: size('screenWidth'),
    screenHeight: size('screenHeight'),
    windowWidth: size('windowWidth'),
    windowHeight: size('windowHeight'),
    viewportWidth: size('viewportWidth'),
    viewportHeight: size('viewportHeight'),
    pixelRatio,
  };
}
